import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import axiosInstance from "../../../Config/axiosInstance";
import AddClientContacts from "./AddClientContacts";
import "./AddClientDialog.css";

const initialFormData = {
  clientCode: "",
  cui: "",
  clientName: "",
  address: "",
  phone: "",
  status: "",
  responsiblePerson: "",
  contacts: [],
};

const initialErrors = {
  clientCode: "",
  cui: "",
  name: "",
  address: "",
  phone: "",
  status: "",
  responsiblePerson: "",
  submit: "",
};

export default function AddClient({
  isOpen,
  onClose,
  onAddContact,
  fetchClients,
  setCreatedClientId,
}) {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [employees, setEmployees] = useState([]);
  const animatedComponents = useMemo(() => makeAnimated(), []);

  // Opțiunile disponibile pentru statusul clientului.
  const statusOptions = useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
      { value: "Prospect", label: "Prospect" },
      { value: "Blocked", label: "Blocked" },
    ],
    []
  );

  // Transformă lista de angajați în opțiuni pentru react-select.
  const employeeOptions = useMemo(
    () =>
      employees.map((employee) => ({
        value: employee.id,
        label: `${employee.first_name} ${employee.last_name}`,
      })),
    [employees]
  );

  // Caută în lista de opțiuni elementul corespunzător unei valori salvate în state.
  const findOption = useCallback(
    (options, value) =>
      options.find((option) => String(option.value) === String(value)) || null,
    []
  );

  // Blochează toate controalele în timpul salvării.
  const disabledAll = isSubmitting;

  // Stiluri custom pentru react-select.
  const selectMenuStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 20000 }),
      menu: (base) => ({ ...base, zIndex: 20000 }),
      control: (base, state) => ({
        ...base,
        minHeight: "40px",
        borderRadius: "12px",
        borderWidth: "1.5px",
        borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
        boxShadow: state.isFocused
          ? "0 0 0 4px rgba(59, 130, 246, 0.1), 0 4px 8px -4px rgba(59, 130, 246, 0.3)"
          : "0 2px 4px rgba(0, 0, 0, 0.02)",
        "&:hover": {
          borderColor: "#3b82f6",
        },
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
      }),
    }),
    []
  );

  // Formatează mesajele astfel încât primul caracter să fie mare.
  const capitalizeFirstLetter = useCallback((text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  }, []);

  // Resetează complet formularul la valorile inițiale.
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors(initialErrors);
    setShowContactForm(false);
    setIsSubmitting(false);
    setSubmitted(false);
  }, []);

  // Când dialogul principal se deschide, formularul este resetat, astfel încât utilizatorul să pornească mereu cu un form curat.
  useEffect(() => {
    if (isOpen) {
      resetForm();

      axiosInstance
        .get("/employees/")
        .then((response) => setEmployees(response.data))
        .catch((error) => console.error("Error fetching employees:", error));
    }
  }, [isOpen, resetForm]);

  // Închidere forțată a dialogului: resetează stările locale și apelează callback-ul primit din părinte.
  const forceClose = useCallback(() => {
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  // Închidere controlată a dialogului.
  const handleClose = useCallback(() => {
    if (disabledAll) return;
    forceClose();
  }, [disabledAll, forceClose]);

  // Extrage într-un mod generic și sigur mesajul de eroare primit de la API.
  const extractErrorMessage = useCallback((error) => {
    const data = error?.response?.data;

    if (typeof data === "string") return data;
    if (data?.detail) return data.detail;
    if (data?.message) return data.message;

    if (data && typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      if (firstKey) {
        const value = data[firstKey];
        if (Array.isArray(value)) return value[0];
        if (typeof value === "string") return value;
      }
    }

    return error?.message || "An unexpected error occurred";
  }, []);

  // Validează toate câmpurile obligatorii înainte de trimitere..
  const validateForm = useCallback(() => {
    const nextErrors = { ...initialErrors };

    if (!formData.clientCode.trim()) {
      nextErrors.clientCode = "Client code is required";
    } else if (formData.clientCode.trim().length !== 3) {
      nextErrors.clientCode = "Client code must be exactly 3 characters";
    }

    if (!formData.cui.trim()) {
      nextErrors.cui = "CUI is required";
    }

    if (!formData.clientName.trim()) {
      nextErrors.name = "Client name is required";
    }

    if (!formData.address.trim()) {
      nextErrors.address = "Address is required";
    }

    if (!formData.phone.trim()) {
      nextErrors.phone = "Phone is required";
    }

    if (!formData.status) {
      nextErrors.status = "Status is required";
    }

    if (!formData.responsiblePerson) {
      nextErrors.responsiblePerson = "Responsible person is required";
    }

    setErrors((prev) => ({
      ...prev,
      ...nextErrors,
      submit: "",
    }));

    return (
      !nextErrors.clientCode &&
      !nextErrors.cui &&
      !nextErrors.name &&
      !nextErrors.address &&
      !nextErrors.phone &&
      !nextErrors.status &&
      !nextErrors.responsiblePerson
    );
  }, [formData]);

  // Gestionează modificările pentru input-urile clasice.
  // Pentru clientCode aplică reguli speciale:
  // - doar litere
  // - uppercase
  // - maxim 3 caractere
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === "clientCode") {
      const uppercaseValue = value
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .slice(0, 3);

      setFormData((prev) => ({
        ...prev,
        [name]: uppercaseValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name === "clientName" ? "name" : name]: "",
      submit: "",
    }));
  }, []);

  // Gestionează select-urile simple, cum este câmpul status.
  // Salvează valoarea selectată în formData și șterge eroarea aferentă.
  const handleSelectChange = useCallback((selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      submit: "",
    }));
  }, []);

  // Gestionează multi-selectul pentru contacte.
  // Păstrează în stare doar contactele selectate efectiv din listă.
  const handleMultiSelectChange = useCallback((selectedOptions) => {
    const selected = selectedOptions || [];
    const selectedValues = new Set(selected.map((item) => item.value));

    setFormData((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((contact) =>
        selectedValues.has(contact.value || contact.email)
      ),
    }));
  }, []);

  // Adaugă un contact nou creat din dialogul secundar în lista locală.
  // Contactul primește temporar un value unic pentru a putea fi folosit în select.
  const handleAddContactToList = useCallback(async (newContact) => {
    const tempContact = {
      ...newContact,
      value: `temp-${Date.now()}`,
      label: `${newContact.name} - ${newContact.position}`,
      status: "Active",
    };

    setFormData((prev) => ({
      ...prev,
      contacts: [...prev.contacts, tempContact],
    }));

    setShowContactForm(false);
  }, []);

  // Deschide formularul secundar pentru adăugarea unui contact nou.
  const handleShowContactForm = useCallback(() => {
    if (disabledAll) return;
    setShowContactForm(true);
  }, [disabledAll]);

  // Revine din formularul de contact în formularul principal de client.
  const handleBackToClientForm = useCallback(() => {
    if (disabledAll) return;
    setShowContactForm(false);
  }, [disabledAll]);

  // Funcția principală de submit:
  // 1. previne submit-ul default
  // 2. validează formularul
  // 3. creează clientul prin API
  // 4. salvează contactele asociate, dacă există
  // 5. reîncarcă lista de clienți
  // 6. închide dialogul la succes
  // Dacă apare o eroare, încearcă să o mapeze pe câmpurile formularului
  // și/sau pe mesajul general de submit.
  const handleAddClient = useCallback(
    async (e) => {
      if (e?.preventDefault) {
        e.preventDefault();
      }

      setSubmitted(true);
      setErrors(initialErrors);

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      const newClient = {
        name: formData.clientName,
        address: formData.address,
        phone: formData.phone,
        status: formData.status,
        cui: formData.cui,
        code_suffix: formData.clientCode,
        responsible_person: formData.responsiblePerson,
      };

      try {
        const response = await axiosInstance.post("/clients/", newClient);

        if (response.status === 201 || response.status === 200) {
          const newClientId = response.data.id;
          setCreatedClientId?.(newClientId);

          if (formData.contacts.length > 0) {
            const contactPromises = formData.contacts.map(async (contact) => {
              try {
                const { value, label, status, ...contactData } = contact;

                const contactWithClient = {
                  ...contactData,
                  client: newClientId,
                  contact_status: "Active",
                };

                const contactResponse = await axiosInstance.post(
                  "/client-contacts/",
                  contactWithClient
                );

                return contactResponse.data;
              } catch (contactError) {
                console.error("Failed to save contact:", contact.name, contactError);
                console.error(
                  "Contact error response:",
                  contactError.response?.data
                );
                return null;
              }
            });

            await Promise.allSettled(contactPromises);
          }

          await fetchClients?.();

          forceClose();
        }
      } catch (error) {
        console.error("Error adding client:", error);
        console.error("Error response:", error.response?.data);

        const responseData = error?.response?.data;

        if (responseData && typeof responseData === "object") {
          setErrors((prev) => ({
            ...prev,
            clientCode: responseData.code_suffix?.[0] || "",
            cui: responseData.cui?.[0] || "",
            name: responseData.name?.[0] || "",
            address: responseData.address?.[0] || "",
            phone: responseData.phone?.[0] || "",
            status: responseData.status?.[0] || "",
            responsiblePerson: responseData.responsible_person?.[0] || "",
            submit: extractErrorMessage(error),
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            submit: extractErrorMessage(error),
          }));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      formData,
      setCreatedClientId,
      fetchClients,
      extractErrorMessage,
      forceClose,
    ]
  );

  // Transformă contactele din state în formatul cerut de react-select.
  const contactOptions = useMemo(
    () =>
      formData.contacts.map((contact) => ({
        value: contact.value || contact.email,
        label: contact.label || `${contact.name} - ${contact.position}`,
      })),
    [formData.contacts]
  );

  // Valorile selectate în multi-select sunt aceleași contacte din state,
  // mapate în formatul necesar componentului Select.
  const contactValues = useMemo(
    () =>
      formData.contacts.map((contact) => ({
        value: contact.value || contact.email,
        label: contact.label || `${contact.name} - ${contact.position}`,
      })),
    [formData.contacts]
  );

  // Variabile ajutătoare pentru afișarea erorilor doar după ce utilizatorul a încercat să trimită formularul.
  const showClientCodeError = submitted && !!errors.clientCode;
  const showCuiError = submitted && !!errors.cui;
  const showNameError = submitted && !!errors.name;
  const showAddressError = submitted && !!errors.address;
  const showPhoneError = submitted && !!errors.phone;
  const showStatusError = submitted && !!errors.status;
  const showResponsiblePersonError = submitted && !!errors.responsiblePerson;

  return (
    <>
      <Dialog
        open={isOpen && !showContactForm}
        onClose={disabledAll ? undefined : handleClose}
        maxWidth="sm"
        fullWidth
        className="add-client-dialog"
      >
        <DialogTitle className="acd-title">
          <span className="acd-header-title">Add Client</span>
        </DialogTitle>

        <DialogContent dividers className="acd-content">
          <form onSubmit={handleAddClient} className="acd-form">
            <div className="acd-form-group">
              <label className="acd-label">
                Client Code <span style={{ color: "red" }}>*</span>
                <small className="acd-label-hint">(3 characters)</small>
              </label>
              <input
                type="text"
                name="clientCode"
                value={formData.clientCode}
                onChange={handleChange}
                maxLength={3}
                disabled={disabledAll}
                className={`acd-input ${
                  showClientCodeError ? "acd-input-error" : ""
                }`}
                placeholder="ABC"
                style={{ textTransform: "uppercase" }}
              />
              {showClientCodeError && (
                <div className="acd-field-error">{errors.clientCode}</div>
              )}
            </div>

            <div className="acd-form-group">
              <label className="acd-label">
                CUI <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="cui"
                value={formData.cui}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${showCuiError ? "acd-input-error" : ""}`}
                placeholder="Enter CUI"
              />
              {showCuiError && (
                <div className="acd-field-error">
                  {capitalizeFirstLetter(errors.cui)}
                </div>
              )}
            </div>

            <div className="acd-form-group">
              <label className="acd-label">
                Client Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${showNameError ? "acd-input-error" : ""}`}
                placeholder="Enter client name"
              />
              {showNameError && (
                <div className="acd-field-error">{errors.name}</div>
              )}
            </div>

            <div className="acd-form-group">
              <label className="acd-label">
                Address <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${
                  showAddressError ? "acd-input-error" : ""
                }`}
                placeholder="Enter address"
              />
              {showAddressError && (
                <div className="acd-field-error">{errors.address}</div>
              )}
            </div>

            <div className="acd-form-group">
              <label className="acd-label">
                Phone <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${
                  showPhoneError ? "acd-input-error" : ""
                }`}
                placeholder="Enter phone number"
              />
              {showPhoneError && (
                <div className="acd-field-error">{errors.phone}</div>
              )}
            </div>

            <div className="acd-form-group">
              <label className="acd-label">
                Status <span style={{ color: "red" }}>*</span>
              </label>

              <Select
                name="status"
                options={statusOptions}
                value={findOption(statusOptions, formData.status)}
                onChange={handleSelectChange}
                placeholder="Select Status"
                isClearable
                isDisabled={disabledAll}
                classNamePrefix="acd-react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectMenuStyles}
                className={showStatusError ? "acd-select-error" : ""}
              />

              {showStatusError && (
                <div className="acd-field-error">{errors.status}</div>
              )}
            </div>

            <div className="acd-form-group">
              <label className="acd-label">
                Responsible Person <span style={{ color: "red" }}>*</span>
              </label>

              <Select
                name="responsiblePerson"
                options={employeeOptions}
                value={findOption(employeeOptions, formData.responsiblePerson)}
                onChange={(option) =>
                  handleSelectChange(option, { name: "responsiblePerson" })
                }
                placeholder="Select Responsible Person"
                isClearable
                isSearchable
                isDisabled={disabledAll}
                classNamePrefix="acd-react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectMenuStyles}
                className={showResponsiblePersonError ? "acd-select-error" : ""}
              />

              {showResponsiblePersonError && (
                <div className="acd-field-error">
                  {errors.responsiblePerson}
                </div>
              )}
            </div>

            <div className="acd-form-group">
              <label className="acd-label">Contact Persons</label>

              <div className="acd-contact-row">
                <div className="acd-contact-select">
                  <Select
                    options={contactOptions}
                    value={contactValues}
                    isMulti
                    components={animatedComponents}
                    classNamePrefix="acd-react-select"
                    isClearable
                    isDisabled={formData.contacts.length === 0 || disabledAll}
                    placeholder={
                      formData.contacts.length === 0
                        ? "Add Contact"
                        : "Select contacts..."
                    }
                    noOptionsMessage={() =>
                      "No contacts available - add a contact first"
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      ...selectMenuStyles,
                      control: (base, state) => ({
                        ...selectMenuStyles.control(base, state),
                        backgroundColor:
                          formData.contacts.length === 0 || disabledAll
                            ? "#f5f5f5"
                            : "#ffffff",
                        borderColor:
                          formData.contacts.length === 0 || disabledAll
                            ? "#e0e0e0"
                            : state.isFocused
                            ? "#3b82f6"
                            : "#e2e8f0",
                        cursor:
                          formData.contacts.length === 0 || disabledAll
                            ? "not-allowed"
                            : "default",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color:
                          formData.contacts.length === 0 || disabledAll
                            ? "#9e9e9e"
                            : "#94a3b8",
                      }),
                    }}
                    onChange={handleMultiSelectChange}
                  />
                </div>

                <Button
                  type="button"
                  onClick={handleShowContactForm}
                  disabled={disabledAll}
                  className="acd-btn acd-btn-add-contact"
                >
                  Add Contact
                </Button>
              </div>
            </div>

            {errors.submit && (
              <div className="acd-submit-error">{errors.submit}</div>
            )}
          </form>
        </DialogContent>

        <DialogActions className="acd-actions">
          <Button
            onClick={handleClose}
            disabled={disabledAll}
            className="acd-btn acd-btn-cancel"
          >
            Close
          </Button>

          <Button
            onClick={handleAddClient}
            disabled={disabledAll}
            variant="contained"
            className="acd-btn acd-btn-create"
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} className="acd-spinner" />
              ) : null
            }
          >
            {isSubmitting ? "Saving..." : "Add Client"}
          </Button>
        </DialogActions>
      </Dialog>

      {isOpen && showContactForm && (
        <AddClientContacts
          isOpen={showContactForm}
          onClose={handleBackToClientForm}
          onAddContact={handleAddContactToList}
          clientId={null}
        />
      )}
    </>
  );
}